import React, { useCallback, useRef } from 'react';
import { Paperclip, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useLmxStore } from './lmxStore';
import { uploadToR2 } from '../utils/uploadToR2';

/**
 * Envío LMX-3 · adjuntar imágenes a LMX: botón, arrastrar y pegar.
 * Cada imagen se sube a R2 con /api/files/upload (que ya la registra como
 * asset del usuario) y viaja en el próximo pedido como {asset_id, name}.
 * El backend verifica que el asset sea del usuario y se la muestra al modelo.
 */
export const MAX_ATTACHMENTS = 4;
const MAX_BYTES = 15 * 1024 * 1024;

export const useLmxAttach = () => {
  const store = useLmxStore;

  const addFiles = useCallback(async (fileList) => {
    const files = Array.from(fileList || []).filter((f) => f?.type?.startsWith('image/'));
    if (!files.length) {
      toast.info('Por ahora LMX recibe imágenes (JPG, PNG, WEBP).');
      return;
    }
    const room = MAX_ATTACHMENTS - store.getState().attachments.length;
    if (room <= 0) {
      toast.info(`Hasta ${MAX_ATTACHMENTS} imágenes por pedido.`);
      return;
    }
    files.slice(0, room).forEach(async (file) => {
      if (file.size > MAX_BYTES) {
        toast.error(`${file.name} pesa más de 15 MB.`);
        return;
      }
      const key = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const preview = URL.createObjectURL(file);
      store.getState().addAttachment({
        key, name: file.name || 'imagen', preview, status: 'uploading',
      });
      try {
        const res = await uploadToR2(file);
        if (!res.assetId) throw new Error('sin asset_id');
        store.getState().updateAttachment(key, {
          status: 'ready', assetId: res.assetId, url: res.url,
        });
      } catch (err) {
        store.getState().updateAttachment(key, { status: 'error' });
        toast.error(`No pude subir ${file.name}. Probá de nuevo.`);
      }
    });
  }, [store]);

  const onPaste = useCallback((e) => {
    const files = Array.from(e.clipboardData?.files || []);
    if (files.some((f) => f.type?.startsWith('image/'))) {
      e.preventDefault();
      addFiles(files);
    }
  }, [addFiles]);

  const onDragOver = useCallback((e) => {
    if (e.dataTransfer?.types?.includes('Files')) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
    }
  }, []);

  const onDrop = useCallback((e) => {
    if (!e.dataTransfer?.files?.length) return;
    e.preventDefault();
    addFiles(e.dataTransfer.files);
  }, [addFiles]);

  return { addFiles, onPaste, onDragOver, onDrop };
};

export const LmxAttachButton = ({ onFiles, disabled }) => {
  const inputRef = useRef(null);
  return (
    <>
      <button
        type="button"
        className="lmx-dock-mic"
        onClick={() => inputRef.current?.click()}
        disabled={disabled}
        title="Adjuntar una imagen (también podés arrastrarla o pegarla)"
        aria-label="Adjuntar imagen"
        data-testid="lmx-attach-btn"
      >
        <Paperclip size={14} />
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        hidden
        onChange={(e) => { onFiles(e.target.files); e.target.value = ''; }}
        data-testid="lmx-attach-input"
      />
    </>
  );
};

export const LmxAttachTray = () => {
  const attachments = useLmxStore((s) => s.attachments);
  const remove = useLmxStore((s) => s.removeAttachment);
  if (!attachments.length) return null;
  return (
    <div className="lmx-attach-tray" data-testid="lmx-attach-tray">
      {attachments.map((a) => (
        <div key={a.key} className={`lmx-attach-chip is-${a.status}`} title={a.name}>
          <img src={a.preview || a.url} alt={a.name} />
          {a.status === 'uploading' && <span className="lmx-attach-busy"><Loader2 size={14} /></span>}
          <button type="button" onClick={() => remove(a.key)} aria-label={`Quitar ${a.name}`}>
            <X size={12} />
          </button>
        </div>
      ))}
    </div>
  );
};

export default LmxAttachTray;
